// GoogleAdVertical.tsx
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

declare global {
  interface Window { googleAdVertical?: any[] }
}

type GoogleAdVerticalProps = {
  className?: string;
  adSlot?: string;            // e.g. "8439496639"
  adFormat?: string;          // usually "auto"
  fullWidth?: boolean;        // responsive
  showTestAds?: boolean;      // force test creatives in dev
  refreshOnPath?: boolean;    // refill on route changes
  minHeight?: number;         // visual hint while loading (e.g., 600)
  maxWidth?: number;          // cap container width (e.g., 300)
};

export default function GoogleAdVertical({
  className = "mb-6",
  adSlot = "8439496639",
  adFormat = "auto",
  fullWidth = true,
  showTestAds = process.env.NODE_ENV !== "production",
  refreshOnPath = true,
  minHeight = 600,
  maxWidth = 300,
}: GoogleAdVerticalProps) {
  const { pathname } = useLocation();
  const insRef = useRef<HTMLModElement | null>(null);

  // Remount the <ins> on route changes so AdSense re-fills
  const insKey = refreshOnPath ? `ins-${pathname}-${adSlot}` : `ins-${adSlot}`;

  useEffect(() => {
    const el = insRef.current;
    if (!el) return;

    // Guard: don't push twice (StrictMode, remounts, etc.)
    if (el.getAttribute("data-adsbygoogle-initialized") === "true") return;

    const push = () => {
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        el.setAttribute("data-adsbygoogle-initialized", "true");
      } catch {
        // Benign duplicate errors are expected in dev; ignore
      }
    };

    // Only push once the loader script is on the page (added globally in <head>)
    const hasLoader = !!document.querySelector('script[src*="adsbygoogle.js"]');
    if (hasLoader) {
      const t = setTimeout(push, 0); // next tick after mount
      return () => clearTimeout(t);
    }
  }, [insKey]);

  const testAdProps = showTestAds ? ({ "data-adtest": "on" as const }) : {};

  return (
    <div
      className={`${className} py-2.5`}
      style={{
        maxWidth,
        width: "100%",
        margin: "0 auto",
      }}
    >
      <ins
        key={insKey}
        ref={insRef as any}
        className="adsbygoogle"
        style={{ display: "block", width: "100%", minHeight }}
        data-ad-client="ca-pub-1267919954757840"
        data-ad-slot={adSlot}
        data-ad-format={adFormat}
        data-full-width-responsive={fullWidth ? "true" : "false"}
        {...testAdProps}
      />
    </div>
  );
}
